import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setWebTitle, formatPrice } from '@/lib/format-utils';
import { useMetrics } from '@/hooks/api-hooks';
import PageHeader from '@/components/page-header';
import { SkeletonList } from '@/components/snippets';
import { ServiceTitleStatic } from '@/components/services/service-title';
import {
    Card,
    Select,
    Tabs,
    Text,
    Grid,
    Group,
    Center,
    Badge
} from '@mantine/core';
import {
    IconTrendingUp,
    IconTrendingDown,
    IconUsers,
    IconCalendar,
    IconChartBar,
    IconChartLine
} from '@tabler/icons-react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    LineChart,
    Line,
    AreaChart,
    Area
} from 'recharts';
import dayjs from 'dayjs';
import { TagIcon } from '@/lib/tag-utils';
import { RESERVATION_STATUS } from '@turizi/shared';


export default function MetricsPage(): JSX.Element {
    const { t } = useTranslation();
    setWebTitle(t('metrics_dashboard'));

    const [dateRange, setDateRange] = useState<string>('all_time');


    // Calculate dates based on range selection
    const getDatesForRange = (range: string) => {
        const today = dayjs();
        switch (range) {
            case 'last_30_days':
                return {
                    start: today.subtract(30, 'day').format('YYYY-MM-DD'),
                    end: today.format('YYYY-MM-DD')
                };
            case 'this_month':
                return {
                    start: today.startOf('month').format('YYYY-MM-DD'),
                    end: today.endOf('month').format('YYYY-MM-DD')
                };
            case 'this_year':
                return {
                    start: today.startOf('year').format('YYYY-MM-DD'),
                    end: today.endOf('year').format('YYYY-MM-DD')
                };
            case 'all_time':
            default:
                return {
                    start: '',
                    end: ''
                };
        }
    };

    const dates = getDatesForRange(dateRange);
    const { data: response, isLoading } = useMetrics(
        dates.start && dates.end ? { start: dates.start, end: dates.end } : {}
    );
    const metrics = response?.data;

    // --- Helpers ---
    const MoneyByCurrency = ({
        money,
        className,
        showPercentageOfTotal
    }: {
        money: Record<string, number>,
        className?: string,
        showPercentageOfTotal?: boolean
    }) => {
        if (!money || Object.keys(money).length === 0) {
            return <div className={`text-sm font-semibold text-gray-400 ${className?.includes('text-right') ? 'text-right' : ''}`}>{formatPrice(0)}</div>;
        }
        return (
            <div className="space-y-0.5">
                {Object.entries(money).map(([currency, amount]) => {
                    let pctString = '';
                    if (showPercentageOfTotal && metrics?.totalMoneyIn) {
                        const total = metrics.totalMoneyIn[currency] || 0;
                        const pct = total > 0 ? (amount / total) * 100 : 0;
                        pctString = ` (${pct.toFixed(1)}%)`;
                    }
                    return (
                        <div key={currency} className={className || ''}>
                            {formatPrice(amount)} <span className="text-xs font-semibold text-gray-400 uppercase">{currency}</span>
                            {pctString && <span className="text-xs font-normal text-gray-500 ml-1">{pctString}</span>}
                        </div>
                    );
                })}
            </div>
        );
    };

    const BalanceByCurrency = ({ incomes, egresses, className }: { incomes: Record<string, number>, egresses: Record<string, number>, className?: string }) => {
        const currencies = Array.from(new Set([...Object.keys(incomes || {}), ...Object.keys(egresses || {})]));
        if (currencies.length === 0) {
            return <div className={`text-sm font-semibold text-gray-400 ${className?.includes('text-right') ? 'text-right' : ''}`}>{formatPrice(0)}</div>;
        }
        return (
            <div className="space-y-0.5">
                {currencies.map(curr => {
                    const balance = (incomes?.[curr] || 0) - (egresses?.[curr] || 0);
                    return (
                        <div key={curr} className={className || ''}>
                            {formatPrice(balance)} <span className="text-xs font-semibold text-gray-400 uppercase">{curr}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Prepare charts data
    const servicesBookingsData = metrics?.soldServices?.map((item: any) => ({
        name: item.service ? ServiceTitleStatic(item.service) : item.serviceName,
        id: `#${item.service?.id}`,
        value: item.bookingsCount
    })).sort((a: any, b: any) => b.value - a.value) || [];

    const servicesPaxsData = metrics?.soldServices?.map((item: any) => ({
        name: item.service ? ServiceTitleStatic(item.service) : item.serviceName,
        id: `#${item.service?.id}`,
        value: item.paxsCount || 0
    })).sort((a: any, b: any) => b.value - a.value) || [];

    const servicesMoneyInData = metrics?.soldServices?.map((item: any) => ({
        name: item.service ? ServiceTitleStatic(item.service) : item.serviceName,
        id: `#${item.service?.id}`,
        value: Object.values(item.revenue || {}).reduce((a: any, b: any) => a + Number(b), 0)
    })).sort((a: any, b: any) => b.value - a.value) || [];

    const servicesMoneyOutData = metrics?.soldServices?.map((item: any) => ({
        name: item.service ? ServiceTitleStatic(item.service) : item.serviceName,
        id: `#${item.service?.id}`,
        value: Object.values(item.costs || {}).reduce((a: any, b: any) => a + Number(b), 0)
    })).sort((a: any, b: any) => b.value - a.value) || [];

    const servicesMarginData = metrics?.soldServices?.map((item: any) => ({
        name: item.service ? ServiceTitleStatic(item.service) : item.serviceName,
        id: `#${item.service?.id}`,
        value: Object.values(item.margin || {}).reduce((a: any, b: any) => a + Number(b), 0)
    })).sort((a: any, b: any) => b.value - a.value) || [];

    const clientsChartData = metrics?.salesByClient?.map((client: any) => ({
        name: client.name,
        bookings: client.bookingsCount,
        revenue: Object.values(client.revenue || {}).reduce((a: any, b: any) => a + Number(b), 0)
    })).sort((a: any, b: any) => b.revenue - a.revenue).slice(0, 5) || [];

    const sortedClients = metrics?.salesByClient ? [...metrics.salesByClient].sort((a: any, b: any) => {
        const revA = Number(Object.values(a.revenue || {}).reduce((sum: any, val: any) => sum + Number(val), 0));
        const revB = Number(Object.values(b.revenue || {}).reduce((sum: any, val: any) => sum + Number(val), 0));
        return revB - revA;
    }) : [];

    const sortedServices = metrics?.soldServices ? [...metrics.soldServices].sort((a: any, b: any) => {
        const revA = Number(Object.values(a.revenue || {}).reduce((sum: any, val: any) => sum + Number(val), 0));
        const revB = Number(Object.values(b.revenue || {}).reduce((sum: any, val: any) => sum + Number(val), 0));
        return revB - revA;
    }) : [];

    // --- Daily Time Series ---
    const dailyTimeSeriesData = metrics?.dailyTimeSeries?.map((item: any) => ({
        day: dayjs(item.day).format('DD MMM'),
        fullDay: item.day,
        paxs: item.paxsCount || 0,
        services: item.bookingsCount || 0,
        moneyInVal: Object.values(item.moneyIn || {}).reduce((a: any, b: any) => a + Number(b), 0),
        moneyOutVal: Object.values(item.moneyOut || {}).reduce((a: any, b: any) => a + Number(b), 0),
        moneyIn: item.moneyIn || {},
        moneyOut: item.moneyOut || {}
    })) || [];

    const PaxsTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white border border-slate-200 rounded-lg p-2.5 shadow-md font-sans text-xs">
                    <div className="font-bold text-slate-800 mb-1">{dayjs(data.fullDay).format('DD MMMM, YYYY')}</div>
                    <div className="text-blue-600 font-semibold">
                        {t('passengers')}: <span className=" font-bold text-slate-800">{data.paxs}</span>
                    </div>
                </div>
            );
        }
        return null;
    };

    const ServicesTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white border border-slate-200 rounded-lg p-2.5 shadow-md font-sans text-xs">
                    <div className="font-bold text-slate-800 mb-1">{dayjs(data.fullDay).format('DD MMMM, YYYY')}</div>
                    <div className="text-orange-600 font-semibold">
                        {t('bookings')}: <span className=" font-bold text-slate-800">{data.services}</span>
                    </div>
                </div>
            );
        }
        return null;
    };

    const MoneyTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-md max-w-xs font-sans text-xs">
                    <div className="font-bold text-slate-800 mb-1.5 border-b border-slate-100 pb-1">{dayjs(data.fullDay).format('DD MMMM, YYYY')}</div>

                    {/* Revenue section */}
                    <div className="mb-2">
                        <div className="text-emerald-700 font-semibold mb-0.5 uppercase tracking-wide text-[9px]">{t('total_money_in')}</div>
                        {Object.keys(data.moneyIn).length === 0 ? (
                            <div className="text-slate-500 ">{formatPrice(0)}</div>
                        ) : (
                            Object.entries(data.moneyIn).map(([curr, amt]) => (
                                <div key={curr} className=" text-slate-700 flex justify-between gap-4">
                                    <span>{curr}</span>
                                    <span className="font-semibold">{formatPrice(Number(amt))}</span>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Cost section */}
                    <div>
                        <div className="text-rose-700 font-semibold mb-0.5 uppercase tracking-wide text-[9px]">{t('total_money_out')}</div>
                        {Object.keys(data.moneyOut).length === 0 ? (
                            <div className="text-slate-500 ">{formatPrice(0)}</div>
                        ) : (
                            Object.entries(data.moneyOut).map(([curr, amt]) => (
                                <div key={curr} className=" text-slate-700 flex justify-between gap-4">
                                    <span>{curr}</span>
                                    <span className="font-semibold">{formatPrice(Number(amt))}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title={t('metrics_dashboard')}
                rightSection={
                    <Group justify="flex-end">
                        <Select
                            leftSection={<IconCalendar size={16} />}
                            value={dateRange}
                            onChange={(val) => setDateRange(val || 'this_month')}
                            data={[
                                { value: 'this_month', label: t('this_month') },
                                { value: 'last_30_days', label: t('last_30_days') },
                                { value: 'this_year', label: t('this_year') },
                                { value: 'all_time', label: t('all_time') }
                            ]}
                            style={{ width: 200 }}
                        />
                    </Group>
                }
            />

            <div className="panel-content">
                {isLoading && <SkeletonList />}

                {!isLoading && metrics && (
                    <div className="space-y-6">
                        {/* --- Stats Highlight Row --- */}
                        <Grid align="stretch">
                            {/* Left Column: Reservations by Status Table */}
                            <Grid.Col span={{ base: 12, md: 8 }}>
                                <Card withBorder shadow="none" radius="sm" p="md" className="h-full bg-white flex flex-col">
                                    <Text size="xs" fw={700} c="dimmed" className="uppercase tracking-wider mb-3">
                                        {t('reservations_by_status')}
                                    </Text>
                                    <div className="subtle-table flex-1 mt-2">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th className="pl-2">{t('status')}</th>
                                                    <th className="number">{t('reservations')}</th>
                                                    <th className="number">{t('total_money_in')}</th>
                                                    <th className="number pr-2">{t('total_money_out')}</th>
                                                    <th className="number pr-2">{t('paxs')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(metrics.reservationStatusAggregation || {}).map(([status, data]: any) => (
                                                    <tr key={status}>
                                                        <td className="font-semibold text-gray-800 pl-2">
                                                            <div className="flex items-center gap-2">
                                                                <Badge
                                                                    circle
                                                                    color={RESERVATION_STATUS[status]?.color}
                                                                    className={`!w-[10px] !h-[10px] !p-0`}
                                                                />
                                                                <span>{t(`reservation_${status}`)}</span>
                                                            </div>
                                                        </td>
                                                        <td className="number ">{data.count}</td>
                                                        <td className="number">
                                                            <MoneyByCurrency money={data.moneyIn} className="text-[11px] text-slate-800 font-semibold text-right" />
                                                        </td>
                                                        <td className="number pr-2">
                                                            <MoneyByCurrency money={data.moneyOut} className="text-[11px] text-slate-800 font-semibold text-right" />
                                                        </td>
                                                        <td className="number pr-2">{data.paxs || 'none'}</td>
                                                    </tr>
                                                ))}
                                                {(!metrics.reservationStatusAggregation || Object.keys(metrics.reservationStatusAggregation).length === 0) && (
                                                    <tr>
                                                        <td colSpan={5} className="text-center text-gray-400 py-4">
                                                            {t('no_data')}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card>
                            </Grid.Col>

                            {/* Right Column: Balance & Manual Pricing stacked */}
                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <div className="flex flex-col gap-4 h-full justify-between">
                                    {/* 1. Balance */}
                                    <Card withBorder shadow="none" radius="sm" p="sm" className="bg-white flex-1 flex flex-col justify-between">
                                        <Text size="xs" fw={700} c="dimmed" className="uppercase tracking-wider mb-2">
                                            {t('balance')}
                                        </Text>
                                        <div className="grid grid-cols-3 gap-2 align-baseline text-slate-800">
                                            <div>
                                                <Text size="9px" fw={500} c="dimmed" className="uppercase tracking-wider mb-0.5">{t('total_paid_in')}</Text>
                                                <MoneyByCurrency money={metrics.totalPaidIn} className="text-xs font-semibold text-green-700" />
                                            </div>
                                            <div className="border-l border-slate-100 pl-2">
                                                <Text size="9px" fw={500} c="dimmed" className="uppercase tracking-wider mb-0.5">{t('total_paid_out')}</Text>
                                                <MoneyByCurrency money={metrics.totalPaidOut} className="text-xs font-semibold text-slate-700" />
                                            </div>
                                            <div className="border-l border-slate-100 pl-2">
                                                <Text size="9px" fw={500} c="dimmed" className="uppercase tracking-wider mb-0.5">{t('net_balance')}</Text>
                                                <BalanceByCurrency incomes={metrics.totalPaidIn} egresses={metrics.totalPaidOut} className="text-xs font-semibold text-slate-800" />
                                            </div>
                                        </div>
                                    </Card>

                                    {/* 2. Manual Pricing */}
                                    <Card withBorder shadow="none" radius="sm" p="sm" className="bg-white flex-1 flex flex-col justify-between">
                                        <Text size="xs" fw={700} c="dimmed" className="uppercase tracking-wider mb-1.5">
                                            {t('total_bookings_manual_prices')}
                                        </Text>
                                        <div className="flex items-center justify-between gap-4">
                                            <div>
                                                <div className="text-xl font-bold text-slate-800">
                                                    {metrics.manualPricing?.totalManualBookings || 0}/{metrics.totalBookingsCount || 0}
                                                </div>
                                                <Text size="9px" c="dimmed" className="uppercase mt-0.5">
                                                    {(((metrics.manualPricing?.totalManualBookings || 0) / (metrics.totalBookingsCount || 1)) * 100).toFixed(1)}% {t('total')}
                                                </Text>
                                            </div>
                                            {metrics.manualPricing?.totalManualBookings > 0 && (
                                                <div className="flex-1 flex gap-3 justify-end border-l border-slate-100 pl-3">
                                                    {/* Higher */}
                                                    <div className="flex items-center gap-1">
                                                        <div className="text-emerald-600 bg-emerald-50 p-0.5 rounded-full">
                                                            <IconTrendingUp size={11} />
                                                        </div>
                                                        <div className="text-[10px] font-semibold text-slate-700">
                                                            <span className=" font-bold text-emerald-700">
                                                                {metrics.manualPricing.higherThanOriginalCount || 0}
                                                            </span>
                                                            <span className="text-[9px] text-slate-500 font-medium ml-1">
                                                                ({(((metrics.manualPricing.higherThanOriginalCount || 0) / (metrics.totalBookingsCount || 1)) * 100).toFixed(1)}%)
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Lower */}
                                                    <div className="flex items-center gap-1">
                                                        <div className="text-rose-600 bg-rose-50 p-0.5 rounded-full">
                                                            <IconTrendingDown size={11} />
                                                        </div>
                                                        <div className="text-[10px] font-semibold text-slate-700">
                                                            <span className=" font-bold text-rose-700">
                                                                {metrics.manualPricing.lowerThanOriginalCount || 0}
                                                            </span>
                                                            <span className="text-[9px] text-slate-500 font-medium ml-1">
                                                                ({(((metrics.manualPricing.lowerThanOriginalCount || 0) / (metrics.totalBookingsCount || 1)) * 100).toFixed(1)}%)
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Card>

                                    {/* 3. Client as Passenger deprecated not usefukll */}
                                    {/* <Card withBorder shadow="none" radius="sm" p="sm" className="bg-white flex-1 flex flex-col justify-between">
                                        <Text size="xs" fw={700} c="dimmed" className="uppercase tracking-wider mb-1.5">
                                            {t('client_as_pax')}
                                        </Text>
                                        <div className="flex items-center justify-between gap-4">
                                            <div>
                                                <div className="text-xl font-bold text-slate-800">
                                                    {metrics.clientIsPax?.clientIsPaxCount || 0}/{metrics.clientIsPax?.totalEligibleReservations || 0}
                                                </div>
                                                <Text size="9px" c="dimmed" className="uppercase mt-0.5">
                                                    {(metrics.clientIsPax?.percentage || 0).toFixed(1)}% {t('client_as_pax_pct_desc')}
                                                </Text>
                                            </div>
                                            <div className="text-slate-400 bg-slate-50 p-1.5 rounded-full">
                                                <IconUsers size={16} />
                                            </div>
                                        </div>
                                    </Card> */}

                                </div>
                            </Grid.Col>
                        </Grid>

                        {/* --- Sold Services Analysis Section --- */}
                        <Card withBorder shadow="sm" radius="md" p="md">
                            <Text fw={600} size="md" mb="md" className="flex items-center gap-2">
                                <IconChartBar size={18} /> {t('sold_services')}
                            </Text>

                            <Grid>
                                {/* 1. Bookings */}
                                <Grid.Col span={{ base: 12, md: 6 }}>
                                    <div className="border border-slate-100 rounded-sm p-3 bg-slate-50/20">
                                        <Text fw={500} size="xs" c="dimmed" mb="sm" className="uppercase tracking-wider">
                                            {t('services_sold_by_bookings')}
                                        </Text>
                                        {servicesBookingsData.length === 0 ? (
                                            <Center className="h-[180px]">
                                                <Text c="dimmed" size="xs">{t('no_data')}</Text>
                                            </Center>
                                        ) : (
                                            <ResponsiveContainer width="100%" height={180}>
                                                <BarChart data={servicesBookingsData} margin={{ bottom: 10, left: -25, right: 10 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                                    <XAxis dataKey="id" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <Tooltip
                                                        contentStyle={{ borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '10px', maxWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word', padding: '4px 8px' }}
                                                        labelStyle={{ fontWeight: 'bold', color: '#1E293B', marginBottom: '2px' }}
                                                        labelFormatter={(label, items) => {
                                                            const item = items?.[0]?.payload;
                                                            return item ? `${item.name} (${item.id})` : `ID: ${label}`;
                                                        }}
                                                    />
                                                    <Bar dataKey="value" fill="#228BE6" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        )}
                                    </div>
                                </Grid.Col>

                                {/* 2. Passengers */}
                                <Grid.Col span={{ base: 12, md: 6 }}>
                                    <div className="border border-slate-100 rounded-sm p-3 bg-slate-50/20">
                                        <Text fw={500} size="xs" c="dimmed" mb="sm" className="uppercase tracking-wider">
                                            {t('services_sold_by_paxs')}
                                        </Text>
                                        {servicesPaxsData.length === 0 ? (
                                            <Center className="h-[180px]">
                                                <Text c="dimmed" size="xs">{t('no_data')}</Text>
                                            </Center>
                                        ) : (
                                            <ResponsiveContainer width="100%" height={180}>
                                                <BarChart data={servicesPaxsData} margin={{ bottom: 10, left: -25, right: 10 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                                    <XAxis dataKey="id" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <Tooltip
                                                        contentStyle={{ borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '10px', maxWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word', padding: '4px 8px' }}
                                                        labelStyle={{ fontWeight: 'bold', color: '#1E293B', marginBottom: '2px' }}
                                                        labelFormatter={(label, items) => {
                                                            const item = items?.[0]?.payload;
                                                            return item ? `${item.name} (${item.id})` : `ID: ${label}`;
                                                        }}
                                                    />
                                                    <Bar dataKey="value" fill="#FD7E14" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        )}
                                    </div>
                                </Grid.Col>

                                {/* 3. Money In (Revenue) */}
                                <Grid.Col span={{ base: 12, md: 4 }}>
                                    <div className="border border-slate-100 rounded-sm p-3 bg-slate-50/20">
                                        <Text fw={500} size="xs" c="dimmed" mb="sm" className="uppercase tracking-wider">
                                            {t('services_sold_by_money_in')}
                                        </Text>
                                        {servicesMoneyInData.length === 0 ? (
                                            <Center className="h-[180px]">
                                                <Text c="dimmed" size="xs">{t('no_data')}</Text>
                                            </Center>
                                        ) : (
                                            <ResponsiveContainer width="100%" height={180}>
                                                <BarChart data={servicesMoneyInData} margin={{ bottom: 10, left: 15, right: 10 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                                    <XAxis dataKey="id" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <YAxis width={80} tickFormatter={(val) => formatPrice(val)} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <Tooltip
                                                        formatter={(value) => [formatPrice(Number(value)), t('revenue')]}
                                                        contentStyle={{ borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '10px', maxWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word', padding: '4px 8px' }}
                                                        labelStyle={{ fontWeight: 'bold', color: '#1E293B', marginBottom: '2px' }}
                                                        labelFormatter={(label, items) => {
                                                            const item = items?.[0]?.payload;
                                                            return item ? `${item.name} (${item.id})` : `ID: ${label}`;
                                                        }}
                                                    />
                                                    <Bar dataKey="value" fill="#2B8A3E" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        )}
                                    </div>
                                </Grid.Col>

                                {/* 4. Money Out (Costs) */}
                                <Grid.Col span={{ base: 12, md: 4 }}>
                                    <div className="border border-slate-100 rounded-sm p-3 bg-slate-50/20">
                                        <Text fw={500} size="xs" c="dimmed" mb="sm" className="uppercase tracking-wider">
                                            {t('services_sold_by_money_out')}
                                        </Text>
                                        {servicesMoneyOutData.length === 0 ? (
                                            <Center className="h-[180px]">
                                                <Text c="dimmed" size="xs">{t('no_data')}</Text>
                                            </Center>
                                        ) : (
                                            <ResponsiveContainer width="100%" height={180}>
                                                <BarChart data={servicesMoneyOutData} margin={{ bottom: 10, left: 15, right: 10 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                                    <XAxis dataKey="id" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <YAxis width={80} tickFormatter={(val) => formatPrice(val)} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <Tooltip
                                                        formatter={(value) => [formatPrice(Number(value)), t('costs')]}
                                                        contentStyle={{ borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '10px', maxWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word', padding: '4px 8px' }}
                                                        labelStyle={{ fontWeight: 'bold', color: '#1E293B', marginBottom: '2px' }}
                                                        labelFormatter={(label, items) => {
                                                            const item = items?.[0]?.payload;
                                                            return item ? `${item.name} (${item.id})` : `ID: ${label}`;
                                                        }}
                                                    />
                                                    <Bar dataKey="value" fill="#E64980" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        )}
                                    </div>
                                </Grid.Col>

                                {/* 5. Margin */}
                                <Grid.Col span={{ base: 12, md: 4 }}>
                                    <div className="border border-slate-100 rounded-sm p-3 bg-slate-50/20">
                                        <Text fw={500} size="xs" c="dimmed" mb="sm" className="uppercase tracking-wider">
                                            {t('services_sold_by_margin')}
                                        </Text>
                                        {servicesMarginData.length === 0 ? (
                                            <Center className="h-[180px]">
                                                <Text c="dimmed" size="xs">{t('no_data')}</Text>
                                            </Center>
                                        ) : (
                                            <ResponsiveContainer width="100%" height={180}>
                                                <BarChart data={servicesMarginData} margin={{ bottom: 10, left: 15, right: 10 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                                    <XAxis dataKey="id" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <YAxis width={80} tickFormatter={(val) => formatPrice(val)} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <Tooltip
                                                        formatter={(value) => [formatPrice(Number(value)), t('margin')]}
                                                        contentStyle={{ borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '10px', maxWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word', padding: '4px 8px' }}
                                                        labelStyle={{ fontWeight: 'bold', color: '#1E293B', marginBottom: '2px' }}
                                                        labelFormatter={(label, items) => {
                                                            const item = items?.[0]?.payload;
                                                            return item ? `${item.name} (${item.id})` : `ID: ${label}`;
                                                        }}
                                                    />
                                                    <Bar dataKey="value" fill="#15AABF" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        )}
                                    </div>
                                </Grid.Col>
                            </Grid>
                        </Card>

                        {/* --- Daily Evolution Section --- */}
                        <Card withBorder shadow="sm" radius="md" p="md">
                            <Text fw={600} size="md" mb="md" className="flex items-center gap-2">
                                <IconChartLine size={18} /> {t('daily_evolution')}
                            </Text>

                            {dailyTimeSeriesData.length === 0 ? (
                                <Center className="h-[220px]">
                                    <Text c="dimmed" size="xs">{t('no_data')}</Text>
                                </Center>
                            ) : (
                                <Grid>
                                    {/* 1. Passengers Evolution */}
                                    <Grid.Col span={{ base: 12, md: 4 }}>
                                        <div className="border border-slate-100 rounded-sm p-3 bg-slate-50/20">
                                            <Text fw={500} size="xs" c="dimmed" mb="sm" className="uppercase tracking-wider">
                                                {t('paxs_evolution')}
                                            </Text>
                                            <ResponsiveContainer width="100%" height={180}>
                                                <AreaChart data={dailyTimeSeriesData} margin={{ bottom: 10, left: -25, right: 10 }}>
                                                    <defs>
                                                        <linearGradient id="colorPaxs" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#228BE6" stopOpacity={0.4} />
                                                            <stop offset="95%" stopColor="#228BE6" stopOpacity={0.0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                                    <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <Tooltip content={<PaxsTooltip />} />
                                                    <Area type="monotone" dataKey="paxs" stroke="#228BE6" fillOpacity={1} fill="url(#colorPaxs)" strokeWidth={2} />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </Grid.Col>

                                    {/* 2. Services (Bookings) Evolution */}
                                    <Grid.Col span={{ base: 12, md: 4 }}>
                                        <div className="border border-slate-100 rounded-sm p-3 bg-slate-50/20">
                                            <Text fw={500} size="xs" c="dimmed" mb="sm" className="uppercase tracking-wider">
                                                {t('services_evolution')}
                                            </Text>
                                            <ResponsiveContainer width="100%" height={180}>
                                                <AreaChart data={dailyTimeSeriesData} margin={{ bottom: 10, left: -25, right: 10 }}>
                                                    <defs>
                                                        <linearGradient id="colorServices" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#FD7E14" stopOpacity={0.4} />
                                                            <stop offset="95%" stopColor="#FD7E14" stopOpacity={0.0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                                    <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <Tooltip content={<ServicesTooltip />} />
                                                    <Area type="monotone" dataKey="services" stroke="#FD7E14" fillOpacity={1} fill="url(#colorServices)" strokeWidth={2} />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </Grid.Col>

                                    {/* 3. Money (Revenue vs Costs) Evolution */}
                                    <Grid.Col span={{ base: 12, md: 4 }}>
                                        <div className="border border-slate-100 rounded-sm p-3 bg-slate-50/20">
                                            <Text fw={500} size="xs" c="dimmed" mb="sm" className="uppercase tracking-wider">
                                                {t('money_evolution')}
                                            </Text>
                                            <ResponsiveContainer width="100%" height={180}>
                                                <AreaChart data={dailyTimeSeriesData} margin={{ bottom: 10, left: 15, right: 10 }}>
                                                    <defs>
                                                        <linearGradient id="colorMoneyIn" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#2B8A3E" stopOpacity={0.4} />
                                                            <stop offset="95%" stopColor="#2B8A3E" stopOpacity={0.0} />
                                                        </linearGradient>
                                                        <linearGradient id="colorMoneyOut" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#E64980" stopOpacity={0.4} />
                                                            <stop offset="95%" stopColor="#E64980" stopOpacity={0.0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                                    <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <YAxis width={60} tickFormatter={(val) => formatPrice(val)} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                                                    <Tooltip content={<MoneyTooltip />} />
                                                    <Area type="monotone" dataKey="moneyInVal" stroke="#2B8A3E" fillOpacity={1} fill="url(#colorMoneyIn)" strokeWidth={2} />
                                                    <Area type="monotone" dataKey="moneyOutVal" stroke="#E64980" fillOpacity={1} fill="url(#colorMoneyOut)" strokeWidth={2} />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </Grid.Col>
                                </Grid>
                            )}
                        </Card>

                        {/* --- Tables Details Breakdown --- */}
                        <div>
                            <Tabs defaultValue="clients">
                                <Tabs.List mb="md">
                                    <Tabs.Tab value="clients" leftSection={<IconUsers size={16} />}>
                                        {t('clients')}
                                    </Tabs.Tab>
                                    <Tabs.Tab value="services" leftSection={<IconChartBar size={16} />}>
                                        {t('sold_services')}
                                    </Tabs.Tab>
                                </Tabs.List>

                                <Tabs.Panel value="clients">
                                    <div className="subtle-table w-full mt-2">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th className="pl-2">{t('name')}</th>
                                                    <th className="number">{t('bookings_count')}</th>
                                                    <th className="number">{t('percentage')} ({t('bookings')})</th>
                                                    <th className="number pr-2">{t('revenue')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedClients.map((client: any) => (
                                                    <tr key={client.name}>
                                                        <td className="pl-2">
                                                            <div className='flex gap-1 items-center'>
                                                                <TagIcon name={client.category} size='xs' hideText noBackground />
                                                                <div className='font-medium'>{client.name}</div>
                                                                <div className='text-xs text-gray-400'>#{client.id}</div>
                                                            </div>
                                                        </td>
                                                        <td className="number ">{client.bookingsCount}</td>
                                                        <td className="number ">{client.percentageOfBookings?.toFixed(1)}%</td>
                                                        <td className="number pr-2">
                                                            <MoneyByCurrency
                                                                money={client.revenue}
                                                                className="text-[11px] text-slate-800 font-semibold text-right"
                                                                showPercentageOfTotal={true}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                                {sortedClients.length === 0 && (
                                                    <tr>
                                                        <td colSpan={4} className="text-center text-gray-400 py-4">
                                                            {t('no_data')}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </Tabs.Panel>

                                <Tabs.Panel value="services">
                                    <div className="subtle-table mt-2">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th className="pl-2">{t('name')}</th>
                                                    <th className="number">{t('bookings_count')}</th>
                                                    <th className="number pr-2">{t('revenue')}</th>
                                                    <th className="number pr-2">{t('costs')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedServices.map((item: any) => (
                                                    <tr key={item.serviceName}>
                                                        <td>
                                                            <div className="flex text-xs items-start gap-1 font-medium text-slate-800">
                                                                <TagIcon name={item?.service?.category} size="xs" noBackground hideText />
                                                                <div>
                                                                    {ServiceTitleStatic(item?.service)}
                                                                    <span className="text-gray-500 font-light text-[10px] ml-1">
                                                                        #{item?.service?.id}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="number">{item.bookingsCount}</td>
                                                        <td className="number">
                                                            <MoneyByCurrency
                                                                money={item.revenue}
                                                                className="text-[11px] text-slate-800 font-semibold text-right"
                                                                showPercentageOfTotal={true}
                                                            />
                                                        </td>
                                                        <td className="number">
                                                            <MoneyByCurrency
                                                                money={item.costs}
                                                                className="text-[11px] text-slate-800 font-semibold text-right"
                                                                showPercentageOfTotal={true}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                                {sortedServices.length === 0 && (
                                                    <tr>
                                                        <td colSpan={3} className="text-center text-gray-400 py-4">
                                                            {t('no_data')}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </Tabs.Panel>
                            </Tabs>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}